import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import MultiValueInput from "../../inputs/multiValueInput";
import MultiSelector from "../../inputs/MultiSelector";
import SearchDateInput from "../../inputs/searchDateInput";
import {
  clearParameters,
  removeParameter,
  setDateParameters,
  setParameters,
} from "../../redux/global/actions";
import {
  getHwsSnTrailAnalysis,
  getHwsSnTrailsBulk,
} from "../../redux/dynamo_db/actions";
import {
  downloadCSV,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../../utils";
import { hwsTrailAnalysisColumns } from "./hwsTrailAnalysisColumns";
import { DataTable, MaterialSymbol } from "@datalake/towercontrol-banner";
import ExportMenu from "../../widgets/ExportMenu";

const FAMILY = "hwsSnsTrailAnalysis";
const SEARCH_LIMIT = 100;

const statusValues = [
  "To do",
  "Done",
  "Error",
  "Automatic Swap Deactivated",
  "Manual Check Required",
];
const productTypeValues = ["DATA", "VOICE", "UNDEFINED"];

// Columns displayed in the results table (the CSV export carries every column).
const displayedColumns = [
  { name: "hws_identification_date", label: "Creation Date" },
  { name: "hws_order_id", label: "Order ID" },
  { name: "hws_base_order_line", label: "Order Line" },
  { name: "product_type", label: "Product Type" },
  { name: "status", label: "Status" },
  { name: "serial_number_hws_in", label: "SN HWS IN" },
  { name: "part_number_hws_in", label: "PN HWS IN" },
  { name: "hws_in_arrival_date", label: "Arrival Date" },
  { name: "business_partner_hws_in", label: "BP HWS IN" },
  { name: "serial_number_hws_out", label: "SN HWS OUT" },
  { name: "part_number_hws_out", label: "PN HWS OUT" },
  { name: "hws_out_departure_date", label: "Departure Date" },
  { name: "business_partner_hws_out", label: "BP HWS OUT" },
  { name: "swap_date", label: "Swap Date" },
  { name: "replaced_by", label: "Replaced By" },
  { name: "error_reason", label: "Error Reason" },
];

const COLUMN_DESCRIPTIONS = Object.fromEntries(
  hwsTrailAnalysisColumns.map((column) => [column.name, column.description]),
);
const SELECT_FILTER_COLUMNS = ["product_type", "status"];
const TEXT_FILTER_COLUMNS = [
  "hws_order_id",
  "serial_number_hws_in",
  "serial_number_hws_out",
];
const NOWRAP_COLUMNS = [
  "hws_identification_date",
  "hws_order_id",
  "hws_in_arrival_date",
  "hws_out_departure_date",
  "swap_date",
  "serial_number_hws_in",
  "serial_number_hws_out",
];

// DataTable columns: header label with the column description (hwsTrailAnalysisColumns) as tooltip.
const ANALYSIS_COLUMNS = displayedColumns.map((column) => ({
  id: column.name,
  label: COLUMN_DESCRIPTIONS[column.name] ? (
    <Tooltip title={COLUMN_DESCRIPTIONS[column.name]}>
      <span>{column.label}</span>
    </Tooltip>
  ) : (
    column.label
  ),
  ...(SELECT_FILTER_COLUMNS.includes(column.name) && { filter: "select" }),
  ...(TEXT_FILTER_COLUMNS.includes(column.name) && { filter: "text" }),
  ...(NOWRAP_COLUMNS.includes(column.name) && { nowrap: true }),
}));

const modalButtonStyle = { color: "white", background: "var(--ale-primary)" };

function MessageModal({ open, message, onClose }) {
  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogContent>
        <Typography variant="body2">{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" style={modalButtonStyle} onClick={onClose}>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function HwsTrailAnalysis() {
  const dispatch = useDispatch();
  const queryParameters = useSelector((state) => state?.parameters?.[FAMILY]);
  const lines = useSelector((state) =>
    Array.isArray(state?.hwsSnsTrailAnalysis) ? state.hwsSnsTrailAnalysis : [],
  );

  const [currentOrderId, setCurrentOrderId] = React.useState();
  const [currentOrderLine, setCurrentOrderLine] = React.useState();
  const [openDateModal, setOpenDateModal] = React.useState(false);
  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);
  const [openBulkMessage, setOpenBulkMessage] = React.useState(false);

  // An order search when order ids are given, otherwise a date search (the date range is then mandatory).
  const buildQuery = () => {
    const query = {};
    if (queryParameters?.orderIds?.length) {
      query.search_type = "order";
      query.order_id = queryParameters.orderIds;
      if (queryParameters?.orderLines?.length)
        query.order_line = queryParameters.orderLines;
    } else if (
      queryParameters?.creationDateStart &&
      queryParameters?.creationDateEnd
    ) {
      query.search_type = "date";
      query.creation_date_from = queryParameters.creationDateStart;
      query.creation_date_to = queryParameters.creationDateEnd;
    } else {
      return null;
    }
    if (queryParameters?.status?.length) query.status = queryParameters.status;
    if (queryParameters?.productType?.length)
      query.product_type = queryParameters.productType;
    return query;
  };

  const handleExecuteSearch = () => {
    const query = buildQuery();
    if (!query) {
      setOpenDateModal(true);
      return;
    }
    dispatch(getHwsSnTrailAnalysis(query));
  };

  const handleExecuteBulkSearch = () => {
    const query = buildQuery();
    if (!query) {
      setOpenDateModal(true);
      return;
    }
    const infoToken = extractUserInfoFromToken(getLocalStorageSAMLToken());
    dispatch(
      getHwsSnTrailsBulk({
        ...query,
        only_hws_in: false,
        combine: "and",
        email: infoToken.email,
        request_user: infoToken.username,
      }),
    );
    setOpenBulkMessage(true);
  };

  // CSV of the given lines (the rows shown by the table), every column of hwsTrailAnalysisColumns.
  const handleExport = (exportedLines) => {
    const fields = hwsTrailAnalysisColumns.map((column) => ({
      name: column.name,
    }));
    const rows = exportedLines.map((line) => ({
      ...line,
      hws_in_purchased_contracts: line.hws_in_purchased_contracts
        ? JSON.stringify(line.hws_in_purchased_contracts)
        : undefined,
    }));
    downloadCSV(fields, "hardware_support_process_analysis", ",", rows);
  };

  const chipHandlers = (type) => ({
    handleDelete: (chipToDelete) => () =>
      dispatch(removeParameter({ [FAMILY]: { [type]: [chipToDelete] } })),
    handleClear: () => dispatch(clearParameters({ [FAMILY]: type })),
  });

  return (
    <React.Fragment>
      <MessageModal
        open={openDateModal}
        message="The Search Date values are mandatory when no Order ID is given."
        onClose={() => setOpenDateModal(false)}
      />
      <MessageModal
        open={openBulkMessage}
        message="Your query has been sent. You can follow the status in the Bulk Report page or wait for the Rainbow message that will announce the end of the export."
        onClose={() => setOpenBulkMessage(false)}
      />
      <Dialog open={openBulkConfirmation} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            style={modalButtonStyle}
            onClick={() => {
              setOpenBulkConfirmation(false);
              handleExecuteBulkSearch();
            }}
          >
            Yes
          </Button>

          <Button
            variant="text"
            style={modalButtonStyle}
            onClick={() => setOpenBulkConfirmation(false)}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="body2" sx={{ mb: 2 }}>
        Every line of the Hardware Support trail, including the lines that only
        have a replacement (HWS OUT) or only a returned asset (HWS IN). Search
        by creation date, or by order when Order IDs are given; the other
        filters are combined (AND).
      </Typography>

      <MultiValueInput
        setCurrentParam={setCurrentOrderId}
        setQueryParams={setParameters}
        currentParam={currentOrderId}
        queryParams={queryParameters?.orderIds}
        {...chipHandlers("orderIds")}
        label="Order ID"
        family={FAMILY}
        type="orderIds"
      />
      <MultiValueInput
        setCurrentParam={setCurrentOrderLine}
        setQueryParams={setParameters}
        currentParam={currentOrderLine}
        queryParams={queryParameters?.orderLines}
        {...chipHandlers("orderLines")}
        label="Order Line (with an Order ID)"
        family={FAMILY}
        type="orderLines"
      />
      <MultiSelector
        setQueryParams={setParameters}
        queryParams={queryParameters?.status}
        {...chipHandlers("status")}
        label="Status"
        family={FAMILY}
        type="status"
        options={statusValues}
      />
      <MultiSelector
        setQueryParams={setParameters}
        queryParams={queryParameters?.productType}
        {...chipHandlers("productType")}
        label="Product Type"
        family={FAMILY}
        type="productType"
        options={productTypeValues}
      />
      <Grid>
        <SearchDateInput
          title="Creation Date (mandatory without Order ID)"
          setFilterDate={setDateParameters}
          family={FAMILY}
          type="creationDateStart"
          type2="creationDateEnd"
        />
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<MaterialSymbol name="search" size="button" />}
          onClick={handleExecuteSearch}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<MaterialSymbol name="cloud_download" size="button" />}
          onClick={() => setOpenBulkConfirmation(true)}
        >
          Bulk Export
        </Button>
      </Stack>

      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<MaterialSymbol name="expand_more" />}>
          <Typography>Available columns (CSV export)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className="table-header-cell">Field Name</TableCell>
                <TableCell className="table-header-cell">Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hwsTrailAnalysisColumns.map((column) => (
                <TableRow key={column.name} hover>
                  <TableCell>
                    <code>{column.name}</code>
                  </TableCell>
                  <TableCell>{column.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      {lines.length > 0 && (
        <DataTable
          title="Hardware Support Process - Analysis"
          subtitle={
            lines.length === SEARCH_LIMIT
              ? `${lines.length} results, use bulk export for more`
              : `${lines.length} results`
          }
          columns={ANALYSIS_COLUMNS}
          rows={lines}
          getRowId={(row, i) => row?.SK ?? i}
          defaultRowsPerPage={25}
          size="small"
          actions={(rows) => (
            <ExportMenu
              formats={["csv"]}
              disabled={!rows.length}
              onExport={() => handleExport(rows)}
            />
          )}
          sx={{ mt: 3 }}
        />
      )}
    </React.Fragment>
  );
}
